/*
 * Copyright (c) 2022 CalAmp Corp.  All Rights Reserved
 */
import {useEffect, useState} from 'react';
import {
  deleteLocalStorage,
  getLocalStorage,
  setLocalStorage
} from '@ion/core/src/capabilities/storage';
import {isDevelopmentEnvironment} from '@ion/core/src/environment/environment';
import {GenericObject} from '@ion/core/src/types/entities';
import {DLSTheme} from '../types/types';
import createDefaultTheme, {createCustomTheme} from './theme';
import loadExternalTheme, {loadExternalThemeAssets} from './ThemeLoader';
import * as logger from './ThemeLogger';
import {useThemeContext} from './useThemeContext';

/**
 * Defines the `useExternalTheme` hook parameters and type definitions.
 *
 * @param   url The URL from which the external theme.json document is to be retrieved.
 * @param   version The build version to be used when both retrieving the external json
 *          theme, as well as when persisting the externally retrieved theme to cache.
 *          When provided, the version will be appended to the query string of the URL
 *          when retrieving the theme, thus ensuring the theme will be loaded from the
 *          browser cache for the duration of a given release (if not using local storage
 *          for caching). If the theme.json document can change independent of a given
 *          release, this argument should be set to false.
 * @param   useCache When true (default), the retrieved theme document will be persisted
 *          to localStorage for the duration of the release. This ensures the theme will
 *          be loaded immediately and only ever retrieved once per release. If the theme
 *          document can change independent of a given release, this argument should be
 *          set to false.
 * @param   cacheKey Allows for providing an optional key to be used when caching the
 *          theme to localStorage. Defaults to `ion-theme`.
 * @param   includeDefaults When true, indicates that the existing DLS theme.json5
 *          is to be merged with the external theme json document. This allows for
 *          providing a subset of overrides as needed. This parameter defaults to true
 *          as, applications are encouraged to only provide specific overrides where
 *          required in order to reduce redundancy while also leveraging all new
 *          updates defined within the DLS.
 * @param   excludedDomains Allows for providing an optional list of domains from which
 *          the external theme should not be loaded, and only the default theme should
 *          be used. This allows for deploying the same application to any environment
 *          (such as for white labeling) and have bypass attempting to load the external
 *          theme when deployed to the CalAmp specific environment.
 * @param   excludeLocal When true (default), local development addressed will be
 *          added to the exclusions.
 */
export type UseExternalThemeParams = {
  url?: string;
  version?: string;
  useCache?: boolean;
  cacheKey?: string;
  includeDefaults?: boolean;
  excludedDomains?: string[] | readonly string[],
  excludeLocal?: boolean;
};

/**
 * Provides a static reference which can be used to determine if the application
 * is being rendered against an external (custom) theme. This value is used
 * primarily for determining if the application is being rendered against a
 * custom theme which is generally used for providing customer white-labelling
 * for partner co-branding.
 */
let _isCustomTheme = false;

/**
 * Provides a convenience function which determines if the application is being
 * rendered based on an external theme.
 *
 * This value is primarily used for determining if the application is rendered
 * against an external, custom theme, which is generally used for providing
 * customer white-labelling for partner co-branding.
 *
 * ```typescript
 *   import {isCustomTheme} from '@ion/dls/src/theme/theme';
 *   ...
 *
 *   if (isCustomTheme()) {
 *     // conditionally override / implement for custom theme use-case ...
 *   }
 * ```
 *
 * @returns `true` if the application has been rendered with an external theme,
 *          otherwise returns `false` (default).
 */
export const isCustomTheme = (): boolean => (
  _isCustomTheme
);

/**
 * Retrieves the previously cached theme.json document which has been persisted
 * to localStorage (if any) and returns the cached theme if the provided version
 * is the same as the persisted version, otherwise, returns false.
 *
 * ```typescript
 *   import {getCachedTheme} from '@ion/dls/src/theme/theme';
 *   ...
 *   const json = getCachedTheme(version, cacheKey);
 * ```
 *
 * @param   version The version for which the cached theme is to be validated
 *          against. This version is compared against the persisted version
 *          and, if the two values are identical, the cached theme json is
 *          returned.
 * @param   cacheKey The local storage entry to be used when persisting the
 *          theme.
 * @returns The cached theme json document from local storage (if available)
 *          and if the cached version is the same as the provided version,
 *          otherwise, returns false.
 */
export const getCachedTheme = (version: string, cacheKey: string): GenericObject | boolean => {
  const {theme, version: revision} = getLocalStorage(cacheKey) || {};
  if (theme && revision === version) {
    return theme;
  }
  return false;
};

/**
 * Persists the externally retrieved theme json document to local storage for
 * subsequent retrievals, thus allowing for the external theme to only need to
 * be retrieved upon the first load of the application for each release.
 *
 * ```typescript
 *   import {getCachedTheme} from '@ion/dls/src/theme/theme';
 *   ...
 *   const json = getCachedTheme(version, cacheKey);
 * ```
 *
 * @param theme The theme json document which is to be cached / persisted to
 *        local storage for use in subsequent retrievals.
 * @param cacheKey The local storage entry to be used when persisting the
 *        theme.
 * @param version The version of the application at the time the theme json
 *        document was retrieved. This version will be used for determining
 *        if subsequent retrievals should be made from cache or freshly from
 *        the server.
 */
export const setCachedTheme = (theme: GenericObject, version: string, cacheKey: string) => {
  setLocalStorage(cacheKey, {theme, version});
};

/**
 * Provides a utility which determines if the specified domain is allowed to
 * load an external theme.json document based on the list of excluded domains,
 * if any.
 *
 * ```typescript
 *   import {isAllowedDomain} from '@ion/dls/src/theme/theme';
 *
 *   const excludedDomains: string[] = [
 *     'ion.calamp.com',
 *     'ion.calamp.co.uk',
 *     'stg.ion.calamp.com',
 *     'qa.ion.calamp.com',
 *     'dev.ion.calamp.com'
 *   ];
 *
 *   if (isAllowedDomain(excludedDomains)) {
 *     ...
 *   }
 * ```
 *
 * @param   excludedDomains An array of domains from which to be excluded.
 * @param   domain The current domain from which the application is being served.
 * @param   excludeLocal When true (default), local development addressed will be
 *          added to the exclusions.
 * @returns true if the current domain is not in the list of excluded domains,
 *          otherwise, false.
 */
export const isAllowedDomain = (
  excludedDomains?: string[] | readonly string[],
  domain?: string,
  excludeLocal = true): boolean => {
  domain = domain || global.location.host;
  if (domain) {
    const isDevEnv = excludeLocal ? isDevelopmentEnvironment(domain, false) : false;
    if (!isDevEnv) {
      if (excludedDomains) {
        return !excludedDomains.includes(domain);
      }
      return true;
    }
  }
  return false;
};

/**
 * Manages the loading and caching of custom theme resources, specifically the external
 * `theme.json` document as well as the associated theme images, specifically the logo
 * and background image.
 *
 * @see     {@link UseExternalThemeParams}
 * @returns A promise which resolves to the external `theme.json` document, otherwise
 *          returns `null`.
 */
const resolveThemeResources = async(params: UseExternalThemeParams): Promise<GenericObject> => {
  const theme = await resolveExternalTheme(params);
  try {
    logger.assetsLoading();
    await loadExternalThemeAssets();
    logger.assetsLoaded();
  } catch (err) {
    logger.assetsFailed();
  }
  return theme as GenericObject;
};

/**
 * Resolves the external `theme.json` document based on the provided URL as well as
 * the external theme resources, specifically the logo and background image. The
 * external theme can optionally be resolved and persisted to an internal cache
 * by setting `useCache` to `true` and providing a `cacheKey` and version. By
 * default internal caching is disabled.
 *
 * @see {@link UseExternalThemeParams.url}
 * @see {@link UseExternalThemeParams.useCache}
 * @see {@link UseExternalThemeParams.version}
 * @see {@link UseExternalThemeParams.cacheKey}
 */
const resolveExternalTheme = async({
  url, version, useCache, cacheKey
}: UseExternalThemeParams): Promise<GenericObject> => {
  let theme = (useCache && version) ? getCachedTheme(version, cacheKey) : null;
  if (!theme) {
    try {
      logger.themeLoading(url);
      theme = await loadExternalTheme(url);
      if (theme) {
        logger.themeLoaded(url);
        if (useCache) {
          setCachedTheme(theme, version, cacheKey);
          logger.themeCached(url);
        }
      } else {
        logger.themeUnresolved(url);
      }
    } catch (err) {
      logger.themeFailed(url, err);
    }
  }
  return theme as GenericObject;
};

/**
 * The `useExternalTheme` hook allows for retrieving an external theme.json document
 * and creating a DLS theme based on the externally retrieved theme definitions. If
 * the current domain is included in the list of excluded domains, the implementation
 * of externally loading dynamic themes will be bypassed and the default DLS theme
 * will be used.
 *
 * ```typescript
 *   import {useExternalTheme} from '@ion/dls/src/theme/useExternalTheme';
 *   ...
 *
 *   function App({Component, pageProps}: AppProps) {
 *     // retrieve and create the external theme ...
 *     const theme = useExternalTheme({
 *       url      : './theme.json',
 *       version  : getVersion(),
 *       useCache : true
 *     });
 *
 *     ...
 *
 *     // render application against external theme ...
 *     return (
 *       {theme && (
 *         <I18nextProvider i18n={i18n}>
 *           <ThemeProvider theme={theme}>
 *             <ErrorBoundary>
 *               <Component {...pageProps} />
 *             </ErrorBoundary>
 *           </ThemeProvider>
 *         </I18nextProvider>
 *       )}
 *     );
 *   }
 *   export default App;
 * ```
 *
 * @see     {@link UseExternalThemeParams}
 * @returns The DLS Theme instance created from the externally retrieved theme,
 *          or the default theme if the current deployment domain is excluded.
 */
export const useExternalTheme = ({
  url,
  version,
  excludedDomains,
  useCache = true,
  includeDefaults = true,
  excludeLocal = true,
  cacheKey = 'ion-theme'
}: UseExternalThemeParams): DLSTheme => {
  const [theme, setTheme] = useState<DLSTheme>();
  const {
    theme       : selectedTheme,
    isCurrent   : isCurrentTheme,
    multiThemeEnabled
  } = useThemeContext();
  const isCurrentThemeActive = isCurrentTheme && multiThemeEnabled;

  /**
   * Upon initialization as well as when the `url` parameter or current theme
   * changes, the custom theme and theme assets (logo and background image) are
   * loaded and when resolved, the custom theme is created. If the custom theme
   * can not be resolved, the default theme will be used as a fallback.
   *
   * By default the application will attempt to resolve a cached theme which
   * is mapped to the current application version. If resolved, the cached
   * theme will be used, otherwise, the application will attempt to load the
   * external theme.
   */
  useEffect(() => {
    if (isAllowedDomain(excludedDomains, globalThis?.location?.host, excludeLocal)) {
      (async() => {
        const external = await resolveThemeResources({url, version, useCache, cacheKey});
        if (external) {
          _isCustomTheme = true;
          setTheme(createCustomTheme(external as GenericObject, includeDefaults, isCurrentThemeActive));
          logger.themeInitialized();
        } else {
          setTheme(createDefaultTheme());
        }
      })();
    } else {
      deleteLocalStorage(cacheKey);
      setTheme(createDefaultTheme());
    }
  }, [url, selectedTheme, multiThemeEnabled, isCurrentThemeActive]);
  return theme as DLSTheme;
};
